import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Download, X, Check, ExternalLink, Sparkles,
  Clock, MapPin, Mail, Phone, Copy, Star,
  TrendingUp, Users, DollarSign, AtSign,
  ArrowRight, Zap, Brain, Loader2, Lock, Filter
} from 'lucide-react';
import api from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { toast } from 'sonner';
import { SkeletonTable, SkeletonBlock } from '../components/Skeleton';
import { SocialLink, getSocialsFromLead } from '../components/SocialIcons';

const getFilterPresets = (hotThreshold = 70) => [
  { key: 'all', label: 'All', color: 'var(--text-secondary)' },
  { key: 'hot', label: '🔥 Hot', color: 'var(--success)', params: { score_min: hotThreshold } },
  { key: 'warm', label: '🟡 Warm', color: 'var(--warning)', params: { score_min: 40, score_max: hotThreshold - 1 } },
  { key: 'cool', label: '🔵 Cool', color: 'var(--info)', params: { score_max: hotThreshold - 1 } },
  { key: 'email', label: '📧 Has Email', color: 'var(--accent-secondary)', params: { has_email: true } },
  { key: 'phone', label: '✆ Has Phone', color: 'var(--success)', params: { has_phone: true } },
  { key: 'website', label: '🌐 Has Website', color: 'var(--info)', params: { has_website: true } },
  { key: 'high_rated', label: '⭐ 4.5+', color: '#f59e0b', params: { min_rating: 4.5 } },
];

const SORT_OPTIONS = [
  { key: 'lead_score', label: 'Score' },
  { key: 'google_rating', label: 'Rating' },
  { key: 'scraped_at', label: 'Newest' },
  { key: 'company_name', label: 'Company A-Z' },
  { key: 'google_review_count', label: 'Reviews' },
];

export default function HistoryPage() {
  const [searchParams] = useSearchParams();
  const qualityParam = searchParams.get('quality');
  const scoreMinParam = searchParams.get('score_min');
  const scoreMaxParam = searchParams.get('score_max');
  const minRatingParam = searchParams.get('min_rating');
  const dateRangeParam = searchParams.get('date_range');
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState(() => {
    if (qualityParam === 'hot') return 'hot';
    if (scoreMinParam) return 'hot';
    if (scoreMaxParam) return 'cool';
    if (minRatingParam) return 'high_rated';
    if (searchParams.get('has_email') === 'true') return 'email';
    if (searchParams.get('has_phone') === 'true') return 'phone';
    return 'all';
  });
  const [sortBy, setSortBy] = useState('scraped_at');
  const [sortDir, setSortDir] = useState('desc');
  const [resultsPerPage, setResultsPerPage] = useState(20);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [copiedPitch, setCopiedPitch] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingSheets, setExportingSheets] = useState(false);
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  const [exportingIntegration, setExportingIntegration] = useState('');
  const [processingLeads, setProcessingLeads] = useState(new Set());
  const [animated, setAnimated] = useState(false);
  const [hotThreshold, setHotThreshold] = useState(70);
  const [showFilters, setShowFilters] = useState(false);
  const [totalSearches, setTotalSearches] = useState(0);
  const hasActiveFilters = activeFilter !== 'all';
  const [kpiData, setKpiData] = useState({ pipeline: 0, emailRate: 0 });
  
    const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadLeads(page + 1, true, false);
    }
  };
  const searchTimeoutRef = useRef(null);
  const isInitialLoad = useRef(true);
  // Read results_per_page from settings preferences (default 20)
const getResultsPerPage = () => {
  try {
    const saved = localStorage.getItem('ileadx_prefs');
    if (saved) {
      const prefs = JSON.parse(saved);
      return prefs.results_per_page || 50;
    }
  } catch {}
  return 50;
};


  // Restore processing state on mount (survives navigation & refresh)
  const checkPendingOperations = async () => {
    try {
      const res = await api.get('/api/leads/pending-operations');
      const ops = res.data.operations || [];
      if (ops.length > 0) {
        const keys = ops.map(op => {
          const prefix = op.action === 'enrich' ? 'enrich' : 'score';
          return `${op.company_name}_${prefix}`;
        });
        setProcessingLeads(new Set(keys));
        // Poll until all complete
        const poll = setInterval(async () => {
          try {
            const r = await api.get('/api/leads/pending-operations');
            if (!r.data.operations || r.data.operations.length === 0) {
              setProcessingLeads(new Set());
              clearInterval(poll);
            }
          } catch { clearInterval(poll); }
        }, 5000);
        // Cleanup poll after 5 minutes max
        setTimeout(() => clearInterval(poll), 300000);
      }
    } catch {}
  };

  // Initial load
  useEffect(() => {
    loadLeads(1, false, true);
    checkPendingOperations();
    setTimeout(() => setAnimated(true), 200);
    // Load hot threshold from user prefs
    try {
      api.get('/api/auth/me').then(res => {
        if (res.data?.hot_threshold) setHotThreshold(res.data.hot_threshold);
      }).catch(() => {});
    } catch {}
  }, []);

  // Filter/sort/search change,  server-side reload with debounce for search
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    const delay = searchTerm ? 500 : 0; // Debounce search, immediate for filters
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setSelectedLeads(new Set());
      loadLeads(1, false, false);
    }, delay);
    
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, activeFilter, sortBy, sortDir, resultsPerPage]);

  // Manual Load More button replaces infinite scroll

  const loadLeads = async (pageNum = 1, append = false, isInitial = false) => {
    if (isInitial) setLoading(true);
    else if (!append) setIsFiltering(true);
    else setLoadingMore(true);

    try {
      const params = {
        page: pageNum,
        limit: resultsPerPage,
        sort_by: sortBy,
        sort_order: sortDir,
      };
      
      if (searchTerm) params.search = searchTerm;
      
      // Apply URL params from dashboard filters
      if (scoreMinParam) params.score_min = parseInt(scoreMinParam);
      if (scoreMaxParam) params.score_max = parseInt(scoreMaxParam);
      if (minRatingParam) params.min_rating = parseFloat(minRatingParam);
      if (dateRangeParam === '7d') params.date_range = '7d';
      if (dateRangeParam === '30d') params.date_range = '30d';
      if (dateRangeParam === '90d') params.date_range = '90d';
      
      // Apply preset filter params
      const preset = getFilterPresets(hotThreshold).find(f => f.key === activeFilter);
      if (preset?.params) {
        Object.assign(params, preset.params);
      }

      const res = await api.get('/api/leads/history', { params });
      const newLeads = res.data.leads || [];
      const total = res.data.total || 0;
      const more = res.data.has_more || false;

      if (append) {
        setLeads(prev => [...prev, ...newLeads]);
      } else {
        setLeads(newLeads);
      }
      
      setTotalLeads(total);
      setHasMore(more);
      setPage(pageNum);
      
      // Update KPI data on initial load or when filters change (not append)
      if (!append) {
        // KPI calculations from the full loaded dataset
        const allLoaded = append ? [...leads, ...newLeads] : newLeads;
        const searches = res.data.total_searches || [...new Set(allLoaded.map(l => l.search_query).filter(Boolean))].length;
        const pipeline = total * 150;
        const emailRate = total > 0 ? Math.round((allLoaded.filter(l => l.email || l.emails).length / Math.min(allLoaded.length, total)) * 100) : 0;
        setTotalSearches(searches);
        setKpiData({ pipeline, emailRate });
      }
    } catch {
      if (!isInitial) toast('Failed to load leads.', { description: 'Please check your connection and try again.', duration: 5000 });
    } finally {
      setLoading(false);
      setIsFiltering(false);
      setLoadingMore(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Actions
  const selectAll = () => {
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map(l => l.company_name)));
    }
  };

  const toggleSelect = (companyName) => {
    const next = new Set(selectedLeads);
    if (next.has(companyName)) next.delete(companyName);
    else next.add(companyName);
    setSelectedLeads(next);
  };

  const copyPitch = async (lead) => {
    let pitchText = lead.pitch_angle || '';
try {
  if (typeof pitchText === 'string' && pitchText.startsWith('[')) {
    const angles = JSON.parse(pitchText);
    pitchText = (angles[0] || '').replace(/^\[?(Empathy|ROI|Urgency|Data-Driven|Direct|Data)(\s*angle)?[\s:]*[, –-]?\s*/i, '').replace(/\]$/, '').trim();
  }
} catch {}
const success = await copyToClipboard(pitchText);
    if (success) {
      setCopiedPitch(lead.company_name);
      toast.success('Pitch copied!');
      setTimeout(() => setCopiedPitch(null), 2000);
    } else {
      toast('Failed to copy.', { description: 'Please copy the text manually.', duration: 4000 });
    }
  };

  const handleExportGSheet = async () => {
    const data = selectedLeads.size > 0 
      ? leads.filter(l => selectedLeads.has(l.company_name))
      : leads;
    if (data.length === 0) { toast.info('No leads to export'); return; }
    
    setExportingSheets(true);
    try {
      const res = await api.post('/api/leads/export-gsheet', { leads: data, sheet_name: `iLeadX Leads ${new Date().toISOString().slice(0,10)}` });
      if (res.data.success) {
        toast.success(`${data.length} leads exported to Google Sheets!`, {
          action: { label: 'Open Sheet', onClick: () => window.open(res.data.sheet_url, '_blank') },
          duration: Infinity,
          dismissible: true,
        });
        setSelectedLeads(new Set());
      } else {
        toast.error(res.data.error || 'Export failed', { duration: 6000 });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast('Google Sheets not connected.', {
  description: 'Connect it in Integrations to enable exports.',
  duration: 8000,
  action: { label: 'Go to Integrations', onClick: () => navigate('/dashboard/integrations') },
});
      } else {
        toast('Export failed.', { description: 'Please try again or use CSV format instead.', duration: 5000 });
      }
    }
    finally { setExportingSheets(false); }
  };

  const handleExport = async () => {
    const data = selectedLeads.size > 0 
      ? leads.filter(l => selectedLeads.has(l.company_name))
      : leads;
    if (data.length === 0) { toast('No leads selected.', { description: 'Select leads or export all from the list.', duration: 4000 }); return; }
    
    setExporting(true);
    try {
      const res = await api.post('/api/leads/export', { leads: data }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ileadx_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${data.length} leads exported!`);
      setSelectedLeads(new Set());
    } catch { toast('Export failed.', { description: 'Please try again in a moment.', duration: 5000 }); }
    finally { setExporting(false); }
  };

  const handleBulkEnrich = async () => {
    const selected = leads.filter(l => selectedLeads.has(l.company_name));
    if (selected.length === 0) return toast('No leads selected.', { description: 'Check the boxes next to leads you want to process.', duration: 4000 });
    
    const keys = selected.map(l => `${l.company_name}_enrich`);
    setProcessingLeads(prev => new Set([...prev, ...keys]));
    
    let enriched = 0;
    for (const lead of selected) {
      try {
        await api.post('/api/leads/regenerate-score', {
          company_name: lead.company_name,
          search_query: lead.search_query,
          service_context: lead.service_context || 'B2B software',
          enrich_only: true,
        });
        enriched++;
      } catch (err) {
        if (err.response?.status === 429) {
          toast(err.response?.data?.detail || 'Limit reached.', { description: 'Upgrade your plan to continue.', duration: 6000, action: { label: 'View Plans', onClick: () => navigate('/dashboard/billing?tab=plans') }, style: { background: 'transparent', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' } });
          // Clear all processing states
          setProcessingLeads(prev => {
            const next = new Set(prev);
            keys.forEach(k => next.delete(k));
            return next;
          });
          setSelectedLeads(new Set());
          return;
        }
      }
      setProcessingLeads(prev => {
        const next = new Set(prev);
        next.delete(`${lead.company_name}_enrich`);
        return next;
      });
    }
    if (enriched > 0) toast.success(`Enriched ${enriched} lead${enriched > 1 ? 's' : ''}`);
    setSelectedLeads(new Set());
    // Reload leads to show updated data
    setTimeout(() => loadLeads(1, false, false), 2000);
  };

  const handleBulkScore = async () => {
    const selected = leads.filter(l => selectedLeads.has(l.company_name));
    if (selected.length === 0) return toast('No leads selected.', { description: 'Check the boxes next to leads you want to process.', duration: 4000 });
    
    const keys = selected.map(l => `${l.company_name}_score`);
    setProcessingLeads(prev => new Set([...prev, ...keys]));

    let scored = 0;
    
    for (const lead of selected) {
      try {
        await api.post('/api/leads/regenerate-score', {
          company_name: lead.company_name,
          search_query: lead.search_query,
          service_context: lead.service_context || 'B2B software',
        });
        scored++;
      } catch (err) {
        if (err.response?.status === 429) {
          toast(err.response?.data?.detail || 'Limit reached.', { description: 'Upgrade your plan to continue.', duration: 6000, action: { label: 'View Plans', onClick: () => navigate('/dashboard/billing?tab=plans') }, style: { background: 'transparent', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' } });
          // Clear all processing states
          setProcessingLeads(prev => {
            const next = new Set(prev);
            keys.forEach(k => next.delete(k));
            return next;
          });
          setSelectedLeads(new Set());
          return;
        }
      }
      setProcessingLeads(prev => {
        const next = new Set(prev);
        next.delete(`${lead.company_name}_score`);
        return next;
      });
    }
    if (scored > 0) toast.success(`Scored ${scored} lead${scored > 1 ? 's' : ''}`);
    setSelectedLeads(new Set());
    // Reload leads to show updated scores
    setTimeout(() => loadLeads(1, false, false), 2000);
  };

  const selectedCount = selectedLeads.size;

  // ================================================================
  // RENDER
  // ================================================================
  if (loading) {
    return (
      <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 1100px)', margin: '0 auto' }}>
        <SkeletonBlock width="200px" height={28} style={{ marginBottom: 16 }} />
        <SkeletonBlock width="400px" height={16} style={{ marginBottom: 24 }} />
        <SkeletonTable rows={8} cols={5} />
      </div>
    );
  }

  const estimatedPipeline = totalLeads * 150;
  // Approximate email rate from total count
  const emailRate = totalLeads > 0 ? Math.round((kpiData.emailRate || 0)) : 0;

  return (
    <div className="page-content" style={{ maxWidth: '100%', margin: '0 auto' }}>
      
      {/* ================================================================ */}
      {/* WEALTH HEADER */}
      {/* ================================================================ */}
      <div style={{ marginBottom: 'var(--space-2xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 'var(--space-xl)' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 28 }}>📚</span> Lead History
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
              {totalLeads} lead{totalLeads !== 1 ? 's' : ''} across {totalSearches} search{totalSearches !== 1 ? 'es' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/dashboard/new-search')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'linear-gradient(135deg, var(--accent-amber), #D97706)',
                border: 'none',
                padding: '10px 18px', borderRadius: 8,
                color: '#0F172A', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                boxShadow: '0 8px 28px rgba(245,158,11,0.35)',
                whiteSpace: 'nowrap',
              }}
            >
              <Search size={16} /> New Search
            </button>
            <button onClick={selectAll} style={{
              padding: '4px 10px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)', background: 'transparent',
              color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
              {selectedLeads.size === leads.length && leads.length > 0 ? 'Deselect All' : `Select All (${leads.length})`}
            </button>
            <button onClick={handleExport} disabled={exporting} style={{
              padding: '4px 10px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--success)', background: 'transparent',
              color: 'var(--success)', cursor: 'pointer', fontSize: 11, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Download size={12} /> {selectedCount > 0 ? `CSV (${selectedCount})` : 'CSV'}
            </button>
            <button onClick={handleExportGSheet} disabled={exportingSheets} style={{
              padding: '4px 10px', borderRadius: 'var(--radius-md)',
              border: '1px solid #34a853', background: 'transparent',
              color: '#34a853', cursor: exportingSheets ? 'wait' : 'pointer', fontSize: 11, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 4,
              opacity: exportingSheets ? 0.7 : 1,
            }}>
              {exportingSheets ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#34a853', animation: 'loadingDot 1.4s ease-in-out 0s infinite' }} />
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#34a853', animation: 'loadingDot 1.4s ease-in-out 0.2s infinite' }} />
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#34a853', animation: 'loadingDot 1.4s ease-in-out 0.4s infinite' }} />
                </span>
              ) : (
                <img src="https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Sheets_logo_%282014-2020%29.svg" alt="" style={{ width: 12, height: 12 }} />
              )}
              {exportingSheets ? 'Exporting...' : selectedCount > 0 ? `Sheets (${selectedCount})` : 'Sheets'}
            </button>
          </div>
        </div>


      </div>

      {/* ================================================================ */}
      {/* COMMAND BAR */}
      {/* ================================================================ */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--space-lg) var(--space-xl)',
        marginBottom: 'var(--space-xl)',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 'var(--space-md)' }}>
          <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search leads, industries, locations..."
            style={{
              width: '100%', padding: '10px 14px 10px 38px',
              background: 'var(--input-bg)', border: '1px solid var(--input-border)',
              borderRadius: 'var(--radius-md)', color: 'var(--text-primary)',
              fontSize: 'var(--text-sm)', outline: 'none', boxSizing: 'border-box',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--accent-primary)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.boxShadow = 'none'; }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter + Sort — single row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-sm)', alignItems: 'center' }}>
          <button onClick={() => setShowFilters(!showFilters)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)',
            background: showFilters ? 'var(--accent-glow)' : 'transparent',
            color: showFilters ? 'var(--accent-primary)' : 'var(--text-secondary)',
            cursor: 'pointer', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
          }}>
            <Filter size={14} /> Filters
            {hasActiveFilters && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent-primary)' }} />}
          </button>
          
          {hasActiveFilters && (
            <button onClick={() => setActiveFilter('all')} style={{
              padding: '7px 12px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--error)', background: 'transparent',
              color: 'var(--error)', cursor: 'pointer', fontSize: 11,
            }}>Clear</button>
          )}
          
          {hasActiveFilters && (
            <span style={{
              padding: '3px 10px', borderRadius: 'var(--radius-full)',
              background: `${getFilterPresets(hotThreshold).find(f => f.key === activeFilter)?.color || 'var(--text-secondary)'}15`,
              color: getFilterPresets(hotThreshold).find(f => f.key === activeFilter)?.color || 'var(--text-secondary)',
              fontSize: 11, fontWeight: 600,
            }}>
              {getFilterPresets(hotThreshold).find(f => f.key === activeFilter)?.label || activeFilter}
            </span>
          )}

          {/* Spacer */}
          <span style={{ flex: 1 }} />

          {/* Sort */}
          <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500 }}>Sort:</span>
          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setSortDir('desc'); }} style={{
            background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', fontSize: 11,
            border: '1px solid var(--border-primary)', borderRadius: 6, padding: '5px 8px',
            cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
          }}>
            {SORT_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
          
          <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} style={{
            background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', fontSize: 11,
            border: '1px solid var(--border-primary)', borderRadius: 6, padding: '5px 8px',
            cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
          }}>
            {sortDir === 'desc' ? '↓' : '↑'}
          </button>
          
          <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 500, marginLeft: 8, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Show:
            <select value={resultsPerPage} onChange={e => setResultsPerPage(Number(e.target.value))} style={{
              background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', fontSize: 11,
              border: '1px solid var(--border-primary)', borderRadius: 6, padding: '5px 8px',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
            }}>
              {[20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        </div>

        {/* Expanded Filters Dropdown */}
        {showFilters && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 'var(--space-md)',
            padding: 'var(--space-sm) var(--space-md)', background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)',
          }}>
            {getFilterPresets(hotThreshold).map(preset => (
              <button key={preset.key} onClick={() => { setActiveFilter(preset.key); setShowFilters(false); }} style={{
                padding: '6px 12px', borderRadius: 'var(--radius-md)',
                border: activeFilter === preset.key ? `2px solid ${preset.color}` : '1px solid var(--border-primary)',
                background: activeFilter === preset.key ? `${preset.color}15` : 'var(--bg-secondary)',
                color: activeFilter === preset.key ? preset.color : 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 12, fontWeight: activeFilter === preset.key ? 600 : 500,
                transition: 'all 0.15s ease', whiteSpace: 'nowrap',
              }}>
                {preset.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filtering indicator */}
      {isFiltering && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', marginBottom: 8 }}>
          <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {/* ================================================================ */}
      {/* LEAD ROSTER */}
      {/* ================================================================ */}
      {!loading && leads.length === 0 && !isFiltering ? (
        <div style={{
          textAlign: 'center', padding: 'clamp(40px, 8vw, 80px) 24px',
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto var(--space-xl)', fontSize: 32,
          }}>
            📚
          </div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-lg)', fontWeight: 600, margin: '0 0 8px' }}>
            No leads yet
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>
            Your leads will appear here after your first search. Each lead comes with AI scoring, contact details, and a personalized pitch angle ready to copy and send.
          </p>
          <button onClick={() => navigate('/dashboard/new-search')} className="auth-button auth-button-primary" style={{ marginTop: 24, padding: '12px 28px' }}>
            <Search size={16} style={{ marginRight: 8 }} /> Run Your First Search
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {leads
            .filter(lead => {
              // Client-side safety filter,  mirrors server filter
              if (activeFilter === 'hot') return lead.lead_score >= hotThreshold;
              if (activeFilter === 'warm') return lead.lead_score >= 40 && lead.lead_score < hotThreshold;
              if (activeFilter === 'cool') return lead.lead_score !== null && lead.lead_score !== undefined && lead.lead_score !== '' && lead.lead_score < 40;
              if (activeFilter === 'email') return lead.email || lead.emails;
              if (activeFilter === 'phone') return lead.phone || lead.phones;
              if (activeFilter === 'website') return lead.website;
              if (activeFilter === 'high_rated') return lead.google_rating >= 4.5;
              return true; // 'all' filter
            })
            .map((lead, i) => (
              <LeadCard
                key={lead.id || lead.company_name}
                lead={lead}
                index={i}
                hotThreshold={hotThreshold}
                isSelected={selectedLeads.has(lead.company_name)}
                onToggleSelect={() => toggleSelect(lead.company_name)}
                copiedPitch={copiedPitch === lead.company_name}
                onCopyPitch={() => copyPitch(lead)}
                onClick={() => navigate(`/dashboard/company/${encodeURIComponent(lead.company_name)}`)}
                onWebsiteClick={(e) => { e.stopPropagation(); if (lead.website) window.open(lead.website, '_blank'); }}
                isProcessing={processingLeads.has(`${lead.company_name}_enrich`) || processingLeads.has(`${lead.company_name}_score`)}
                animated={animated}
              />
            ))}
          </div>

          {/* Load More button */}
          {hasMore && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <button onClick={handleLoadMore} disabled={loadingMore} style={{
                padding: '12px 32px', borderRadius: 10,
                border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
                color: loadingMore ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                cursor: loadingMore ? 'default' : 'pointer', fontSize: 14, fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { if (!loadingMore) { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--text-primary)'; } }}
              onMouseLeave={e => { if (!loadingMore) { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}>
                {loadingMore ? 'Loading...' : `Load More (${totalLeads - leads.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      {/* ================================================================ */}
      {/* FLOATING BULK ACTION BAR */}
      {/* ================================================================ */}
      {selectedCount > 0 && (
        <>
          <style>{`
            .bulk-action-bar {
              position: fixed;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              background: var(--bg-secondary);
              border: 1px solid var(--border-primary);
              border-radius: var(--radius-xl);
              padding: 10px 20px;
              box-shadow: var(--shadow-xl);
              z-index: 900;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1);
              max-width: calc(100vw - 32px);
              width: auto;
            }
            @media (max-width: 768px) {
              .bulk-action-bar {
                width: 85vw !important;
                left: 7.5vw !important;
                transform: none !important;
                flex-direction: column !important;
                align-items: stretch !important;
                gap: 8px !important;
                padding: 12px 16px !important;
              }
              .bulk-count {
                text-align: center !important;
                font-size: 11px !important;
              }
              .bulk-buttons {
                display: flex !important;
                flex-wrap: wrap !important;
                gap: 6px !important;
                justify-content: center !important;
              }
              .bulk-action-bar button {
                padding: 7px 12px !important;
                font-size: 11px !important;
                gap: 4px !important;
                white-space: nowrap !important;
              }
              .bulk-action-bar button svg {
                width: 13px !important;
                height: 13px !important;
              }
            }
          `}</style>
          <div className="bulk-action-bar">
            <span className="bulk-count" style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {selectedCount} selected
            </span>
            <div className="bulk-buttons" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button onClick={handleBulkEnrich} style={bulkBtnStyle('var(--info)')}>
                <Zap size={14} /> Enrich
              </button>
              <button onClick={handleBulkScore} style={bulkBtnStyle('var(--accent-secondary)')}>
                <Brain size={14} /> Re-score
              </button>
              <button onClick={handleExport} style={bulkBtnStyle('var(--success)')}>
                <Download size={14} /> CSV
              </button>
              <button onClick={handleExportGSheet} disabled={exportingSheets} style={{
                ...bulkBtnStyle('#34a853'), opacity: exportingSheets ? 0.7 : 1, cursor: exportingSheets ? 'wait' : 'pointer',
              }}>
                {exportingSheets ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#34a853', animation: 'loadingDot 1.4s ease-in-out 0s infinite' }} />
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#34a853', animation: 'loadingDot 1.4s ease-in-out 0.2s infinite' }} />
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#34a853', animation: 'loadingDot 1.4s ease-in-out 0.4s infinite' }} />
                  </span>
                ) : (
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Sheets_logo_%282014-2020%29.svg" alt="" style={{ width: 14, height: 14 }} />
                )}
                {exportingSheets ? 'Exporting...' : 'Sheets'}
              </button>
              <button onClick={() => setSelectedLeads(new Set())} style={{
                padding: '8px 14px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ================================================================
// LEAD CARD COMPONENT
// ================================================================
function LeadCard({ lead, index, isSelected, onToggleSelect, copiedPitch, onCopyPitch, onClick, onWebsiteClick, isProcessing, animated, hotThreshold = 70 }) {
  const score = lead.lead_score;
  const scoreLevel = score >= hotThreshold ? 'hot' : score >= 40 ? 'warm' : 'cool';
  const scoreColors = {
    hot: { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success)' },
    warm: { bg: 'var(--warning-bg)', color: 'var(--warning)', border: 'var(--warning)' },
    cool: { bg: 'var(--info-bg)', color: 'var(--info)', border: 'var(--info)' },
  };
  const colors = scoreLevel in scoreColors ? scoreColors[scoreLevel] : scoreColors.cool;
  const socials = getSocialsFromLead(lead);
  const hasEmail = lead.email || lead.emails;
  const hasPhone = lead.phone || lead.phones;
  const hasWebsite = lead.website;

  return (
    <div style={{
      background: isSelected ? 'var(--accent-glow)' : 'var(--bg-secondary)',
      border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
      borderLeft: isSelected ? '3px solid var(--accent-primary)' : `3px solid ${colors.border}`,
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-lg) var(--space-xl)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      opacity: animated ? 1 : 0,
      transform: animated ? 'translateY(0)' : 'translateY(16px)',
      animation: animated ? `fadeSlideIn 0.4s ease ${index * 0.03}s both` : undefined,
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.borderColor = colors.border;
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.borderColor = 'var(--border-primary)';
        e.currentTarget.style.boxShadow = 'none';
      }
    }}>
      <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
        {/* Checkbox */}
        <div onClick={(e) => e.stopPropagation()} style={{ paddingTop: 2 }}>
          <button onClick={onToggleSelect} style={{
            width: 22, height: 22, borderRadius: 6,
            border: isSelected ? '2px solid var(--accent-primary)' : '2px solid var(--border-primary)',
            background: isSelected ? 'var(--accent-primary)' : 'transparent',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s ease', flexShrink: 0, padding: 0,
          }}>
            {isSelected && <Check size={14} color="white" />}
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }} onClick={onClick}>
          {/* Top row: Company + Score */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
            <h3 style={{
              color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 600,
              margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {lead.company_name}
            </h3>
            {score && (
              <span style={{
                padding: '3px 10px', borderRadius: 'var(--radius-full)',
                background: colors.bg, color: colors.color,
                fontSize: 'var(--text-xs)', fontWeight: 700, flexShrink: 0,
              }}>
                {score}
              </span>
            )}
          </div>

          {/* Subtitle: Industry + Location + Rating,  all on one row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, overflow: 'hidden' }}>
            {lead.industry && (
              <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {lead.industry}
              </span>
            )}
            {(lead.city || lead.country) && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                <MapPin size={12} /> {[lead.city, lead.country].filter(Boolean).join(', ')}
              </span>
            )}
            {lead.google_rating && (
              <span style={{ color: '#f59e0b', fontSize: 'var(--text-xs)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                ⭐ {lead.google_rating} <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>({lead.google_review_count || 0})</span>
              </span>
            )}
          </div>

          {/* Pitch angle preview */}
          {lead.pitch_angle && (
            <div style={{
              background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)',
              padding: 'var(--space-sm) var(--space-md)', marginBottom: 10,
              border: '1px solid var(--border-primary)',
              position: 'relative',
            }}>
              {lead.score_locked ? (
                <>
                  <p style={{
                    color: 'var(--text-secondary)', fontSize: 'var(--text-xs)',
                    margin: 0, lineHeight: 1.5, fontStyle: 'italic',
                    filter: 'blur(6px)', userSelect: 'none',
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    "Upgrade to the Growth plan to unlock more AI scoring and personalized pitch angles. Get actionable insights, pain point detection, and ready-to-send outreach messages for every lead."
                  </p>
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, background: 'var(--card-overlay)', borderRadius: 'var(--radius-md)',
                  }}>
                    <Lock size={14} color="var(--text-secondary)" />
                    <a href="/dashboard/billing" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500, textDecoration: 'none', border: '1px solid var(--border-primary)', borderRadius: 4, padding: '2px 8px', background: 'transparent' }}>
                      Upgrade to unlock
                    </a>
                  </div>
                </>
              ) : (
                <p style={{
                  color: 'var(--text-secondary)', fontSize: 'var(--text-xs)',
                  margin: 0, lineHeight: 1.5, fontStyle: 'italic',
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {(() => {
  try {
    const p = lead.pitch_angle;
    if (typeof p === 'string' && p.startsWith('[')) {
      const angles = JSON.parse(p);
      // Find first non-empty angle after cleaning
      let first = '';
      for (const a of angles) {
        if (a && a.trim()) {
          first = a.replace(/^\[?(Empathy|ROI|Urgency|Data-Driven|Direct|Data)(\s*angle)?[\s:]*[, –-]?\s*/i, '').replace(/\]$/, '').trim();
          if (first) break;
        }
      }
      if (first) return '\"' + first.replace(/^[",\s]+/, '').replace(/[",\s]+$/, '') + '\"';
      return 'No pitch angle yet. Run Enrich & Score to generate one.';
    }
    if (p && p.trim()) return '\"' + p.replace(/^[",\s]+/, '').replace(/[",\s]+$/, '') + '\"';
    return 'No pitch angle yet. Run Enrich & Score to generate one.';
  } catch { return '\"' + ((lead.pitch_angle || '').replace(/^[",\s]+/, '').replace(/[",\s]+$/, '')) + '\"'; }
})()}
                </p>
              )}
            </div>
          )}

          {/* Contact row,  prevent clicks from bubbling */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 10 }} onClick={(e) => e.stopPropagation()}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: hasEmail ? 'var(--success)' : 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
              <Mail size={14} /> {hasEmail ? '' : ''}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: hasPhone ? 'var(--success)' : 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
              <Phone size={14} /> {hasPhone ? '' : ''}
            </span>
            {hasWebsite && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--info)', fontSize: 'var(--text-xs)' }}>
                <ExternalLink size={14} /> Website
              </span>
            )}
            {socials.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', opacity: 1 }} onClick={(e) => e.stopPropagation()}>
                {socials.map((s, i) => (
                  <SocialLink key={i} platform={s.platform} url={s.url} size={14} />
                ))}
              </div>
            )}
          </div>

          {/* Action buttons or Processing spinner */}
          {isProcessing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent-amber)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>
              <span style={{ width: 16, height: 16, border: '2px solid var(--border-primary)', borderTop: '2px solid var(--accent-amber)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              Processing...
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={(e) => { e.stopPropagation(); onCopyPitch(); }} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 14px', borderRadius: 'var(--radius-md)',
                border: `2px solid ${copiedPitch ? 'var(--success)' : 'var(--accent-amber)'}`,
                background: copiedPitch ? 'var(--success-bg)' : 'transparent',
                color: copiedPitch ? 'var(--success)' : 'var(--accent-amber)',
                cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 600,
                transition: 'all 0.2s ease',
              }}>
                {copiedPitch ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Pitch</>}
              </button>
              {hasWebsite && (
                <button onClick={onWebsiteClick} style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 14px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)', background: 'transparent',
                  color: 'var(--text-secondary)', cursor: 'pointer',
                  fontSize: 'var(--text-xs)', fontWeight: 500,
                }}>
                  <ExternalLink size={14} /> Visit
                </button>
              )}
              <button onClick={(e) => { e.stopPropagation(); onClick(); }} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 14px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: 'var(--text-secondary)', cursor: 'pointer',
                fontSize: 'var(--text-xs)', fontWeight: 500,
                marginLeft: 'auto',
              }}>
                Profile <ArrowRight size={14} />
              </button>
              {lead.scraped_at && (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={10} /> {timeAgo(lead.scraped_at)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// HELPERS
// ================================================================
function timeAgo(dateStr) {
  // Ensure timestamp is treated as UTC if no timezone specified
  const date = dateStr && !dateStr.endsWith('Z') && !dateStr.includes('+') ? new Date(dateStr + 'Z') : new Date(dateStr);
  const elapsed = Math.floor((Date.now() - date.getTime()) / 1000);
  if (elapsed < 60) return 'Just now';
  if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ago`;
  if (elapsed < 86400) return `${Math.floor(elapsed / 3600)}h ago`;
  if (elapsed < 604800) return `${Math.floor(elapsed / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const bulkBtnStyle = (color) => ({
  padding: '8px 14px', borderRadius: 'var(--radius-md)',
  border: `2px solid ${color}`, background: 'transparent',
  color, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  display: 'flex', alignItems: 'center', gap: 6,
  whiteSpace: 'nowrap',
});
